import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { toast } from '../hooks/use-toast';
import { loginDestination, postRegisterDestination } from '../lib/authDestination';
import SiteLogo from './SiteLogo';

const field = 'w-full min-h-[48px] rounded-lg border border-slate-300 bg-white px-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/40 focus:border-[#FF6B00]';
const primary = 'w-full min-h-[48px] rounded-full bg-[#FF6B00] text-white font-semibold hover:bg-[#e05f00] disabled:opacity-50 transition-colors';
const linkBtn = 'font-semibold text-[#c2410c] hover:underline min-h-[44px] px-1';

/**
 * Log in / register / email-code / two-factor, in one panel. Uses the same
 * AuthContext methods as the /login, /register and /verify-email pages, so the
 * rules (verification, 2FA, trusted devices) are identical - only where the
 * screens appear changes. Renders inside the slide-in drawer, over the page, so
 * nothing the visitor was doing (like files they just picked) is lost.
 */
const AuthPanel = ({ request, onClose }) => {
  const navigate = useNavigate();
  const { login, register, verifyEmail, resendVerification, verifyLoginTwoFactor, resendLoginTwoFactor } = useAuth();
  const [view, setView] = useState(request.mode === 'register' ? 'register' : 'login');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [challenge, setChallenge] = useState(null); // 2FA: { challengeId, email }
  const [remember, setRemember] = useState(true);
  const [code, setCode] = useState('');
  const [emailSent, setEmailSent] = useState(true);
  const firstField = useRef(null);

  useEffect(() => { firstField.current?.focus(); }, [view]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const go = (v) => { setView(v); setError(''); setCode(''); };

  const finish = (user, kind) => {
    toast({ title: kind === 'register' ? 'Welcome!' : 'Welcome back!', description: `Signed in as ${user?.name || user?.email}` });
    onClose();
    if (request.onSuccess) request.onSuccess(user);
    else navigate(kind === 'register' ? postRegisterDestination() : loginDestination(user?.role));
  };

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try { await fn(); } finally { setBusy(false); }
  };

  const submitLogin = (e) => {
    e.preventDefault();
    run(async () => {
      const r = await login(form.email, form.password);
      if (r.success) return finish(r.user, 'login');
      if (r.requiresTwoFactor) {
        setChallenge({ challengeId: r.challengeId, email: r.email });
        go('twofactor');
        toast({ title: 'Check your email', description: r.message || 'Enter the 6-digit code we sent to finish signing in.' });
        return undefined;
      }
      setError(r.error || 'Login failed');
      return undefined;
    });
  };

  const submitTwoFactor = (e) => {
    e.preventDefault();
    run(async () => {
      const r = await verifyLoginTwoFactor(challenge.email, challenge.challengeId, code, remember);
      if (r.success) return finish(r.user, 'login');
      setError(r.error || 'Verification failed');
      return undefined;
    });
  };

  const resendTwoFactor = () => run(async () => {
    const r = await resendLoginTwoFactor(challenge.email, challenge.challengeId);
    if (r.success) toast({ title: 'Code sent', description: r.message });
    else setError(r.error);
  });

  const submitRegister = (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    run(async () => {
      const r = await register(form.email, form.name, form.password);
      if (!r.success) { setError(r.error || 'Registration failed'); return undefined; }
      if (r.requiresVerification) {
        setEmailSent(!!r.emailSent);
        go('verify');
        return undefined;
      }
      return finish(r.user, 'register');
    });
  };

  const verify = (digits) => run(async () => {
    const r = await verifyEmail(form.email, digits);
    if (r.success) return finish(r.user, 'register');
    setError(r.error || 'Verification failed');
    setCode('');
    return undefined;
  });

  const onCode = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (view === 'verify' && digits.length === 6) verify(digits);
  };

  const resendEmailCode = () => run(async () => {
    const r = await resendVerification(form.email);
    if (r.success) toast({ title: 'Code resent', description: r.emailSent ? 'Check your email for a new code.' : 'A new code has been generated.' });
    else setError(r.error);
  });

  const heading = { login: 'Log in', register: 'Create your account', verify: 'Verify your email', twofactor: 'Extra security check' }[view];

  return (
    <div className="flex-1 overflow-y-auto px-6 py-6" data-testid={`auth-${view}`}>
      <h2 id="auth-drawer-title" className="text-2xl font-bold text-slate-900 mb-1">{heading}</h2>

      {view === 'login' && (
        <form onSubmit={submitLogin} className="mt-5 space-y-4">
          <label className="block text-sm text-slate-600">Email
            <input ref={firstField} type="email" required autoComplete="email" value={form.email} onChange={set('email')}
              className={`${field} mt-1`} data-testid="auth-email" /></label>
          <label className="block text-sm text-slate-600">Password
            <span className="relative block mt-1">
              <input type={showPw ? 'text' : 'password'} required autoComplete="current-password" value={form.password} onChange={set('password')}
                className={`${field} pr-12`} data-testid="auth-password" />
              <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? 'Hide password' : 'Show password'}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 inline-flex items-center justify-center text-slate-500">
                {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
            </span></label>
          {error && <p role="alert" className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
          <button type="submit" disabled={busy} className={primary} data-testid="auth-submit">{busy ? 'Signing in…' : 'Log in'}</button>
          <p className="text-sm text-slate-600 text-center">New here?{' '}
            <button type="button" onClick={() => go('register')} className={linkBtn} data-testid="auth-switch">Create an account</button></p>
        </form>
      )}

      {view === 'twofactor' && (
        <form onSubmit={submitTwoFactor} className="mt-5 space-y-4">
          <p className="text-sm text-slate-600">Enter the 6-digit code sent to <strong>{challenge?.email}</strong>.</p>
          <input ref={firstField} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
            onChange={(e) => onCode(e.target.value)} placeholder="000000" aria-label="Verification code"
            className={`${field} text-center text-xl tracking-[0.4em]`} data-testid="auth-code" />
          <label className="flex items-start gap-3 text-sm text-slate-600">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="mt-1 h-4 w-4" />
            <span>Trust this browser for 30 days so future logins don&apos;t need a code here.</span></label>
          {error && <p role="alert" className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
          <button type="submit" disabled={busy || code.length !== 6} className={primary} data-testid="auth-submit">{busy ? 'Verifying…' : 'Verify and log in'}</button>
          <div className="flex justify-between">
            <button type="button" onClick={resendTwoFactor} disabled={busy} className={linkBtn}>Resend code</button>
            <button type="button" onClick={() => go('login')} className={linkBtn}>Back to log in</button>
          </div>
        </form>
      )}

      {view === 'register' && (
        <form onSubmit={submitRegister} className="mt-5 space-y-4">
          <label className="block text-sm text-slate-600">Full name
            <input ref={firstField} type="text" required autoComplete="name" value={form.name} onChange={set('name')}
              className={`${field} mt-1`} data-testid="auth-name" /></label>
          <label className="block text-sm text-slate-600">Email
            <input type="email" required autoComplete="email" value={form.email} onChange={set('email')}
              className={`${field} mt-1`} data-testid="auth-email" /></label>
          <label className="block text-sm text-slate-600">Password
            <span className="relative block mt-1">
              <input type={showPw ? 'text' : 'password'} required autoComplete="new-password" value={form.password} onChange={set('password')}
                className={`${field} pr-12`} data-testid="auth-password" />
              <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? 'Hide password' : 'Show password'}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 inline-flex items-center justify-center text-slate-500">
                {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
            </span></label>
          <label className="block text-sm text-slate-600">Confirm password
            <input type={showPw ? 'text' : 'password'} required autoComplete="new-password" value={form.confirm} onChange={set('confirm')}
              className={`${field} mt-1`} data-testid="auth-confirm" /></label>
          {error && <p role="alert" className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
          <button type="submit" disabled={busy} className={primary} data-testid="auth-submit">{busy ? 'Creating account…' : 'Create account'}</button>
          <p className="text-xs text-slate-500 text-center">By registering you agree to our Terms &amp; Conditions and Privacy Policy.</p>
          <p className="text-sm text-slate-600 text-center">Already have an account?{' '}
            <button type="button" onClick={() => go('login')} className={linkBtn} data-testid="auth-switch">Log in</button></p>
        </form>
      )}

      {view === 'verify' && (
        <div className="mt-5 space-y-4">
          <p className="text-sm text-slate-600">
            {emailSent ? 'We sent a 6-digit code to ' : 'Enter the verification code for '}<strong>{form.email}</strong>.
          </p>
          {!emailSent && <p className="text-sm text-amber-700">Email isn&apos;t configured on this site. Ask the administrator for your code.</p>}
          <input ref={firstField} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
            onChange={(e) => onCode(e.target.value)} placeholder="000000" aria-label="Verification code" disabled={busy}
            className={`${field} text-center text-xl tracking-[0.4em]`} data-testid="auth-code" />
          {error && <p role="alert" className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
          {busy && <p className="text-sm text-slate-500">Verifying…</p>}
          <div className="flex justify-between">
            <button type="button" onClick={resendEmailCode} disabled={busy} className={linkBtn}>Resend code</button>
            <button type="button" onClick={() => go('register')} className={linkBtn}>Wrong email?</button>
          </div>
        </div>
      )}
    </div>
  );
};

/** Slide-in-from-the-right container: logo at the top, dimmed overlay, Esc / outside click to close. */
const AuthDrawer = ({ request, onClose }) => {
  const { logoUrl, siteName } = useSiteSettings();
  const [shown, setShown] = useState(null);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (request) {
      setShown(request);
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setEntered(true)));
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
    const t = setTimeout(() => setShown(null), 300);
    return () => clearTimeout(t);
  }, [request]);

  useEffect(() => {
    if (!shown) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [shown, onClose]);

  if (!shown) return null;

  return (
    <>
      <div onClick={onClose} aria-hidden="true" data-testid="auth-overlay"
        className={`fixed inset-0 z-[60] bg-black/50 transition-opacity duration-300 ${entered ? 'opacity-100' : 'opacity-0'}`} />
      <aside role="dialog" aria-modal="true" aria-labelledby="auth-drawer-title" data-testid="auth-drawer"
        className={`fixed inset-y-0 right-0 z-[70] w-full max-w-md bg-white text-slate-900 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${entered ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <SiteLogo src={logoUrl} alt={siteName} className="h-10 min-w-[2.5rem] w-auto object-contain text-slate-700 bg-slate-100" />
          <button type="button" onClick={onClose} aria-label="Close" className="w-11 h-11 inline-flex items-center justify-center rounded-full hover:bg-slate-100">
            <X className="w-5 h-5" /></button>
        </div>
        <AuthPanel key={shown.id} request={shown} onClose={onClose} />
      </aside>
    </>
  );
};

export default AuthDrawer;
